// dompurify is hoisted from @shared at runtime via the Vue app's setup. We
// avoid importing it here so the customer-extension files don't pull in a
// workspace-only package directly.

/**
 * Helpers for consuming PG&E rule responses inside cell renderers.
 *
 * The legacy Vue2 PG&E mixin (qadocontractors.newcollab/companymanagement/frontend/src/components/setup.mixin.js)
 * processes rule responses with three command types:
 *   - UPDATE_FIELD_VALUE: { field, newValue, rowId } updates a single cell
 *   - UPDATE_ROW: { rowData, rowId } replaces the whole row
 *   - REMOVE_ROW: { rowId } removes the row
 *
 * For Path B, the Java controller invokes the rule and returns the rule's
 * setOutcomeDescription verbatim. Action rules (e.g. ApproveCompany_UA) emit
 * one of these commands. If the response contains a recognized command, the
 * grid is updated in place. Otherwise the caller falls back to refetch().
 */

/**
 * Attempt to apply a structured rule response to the grid. Returns true if a
 * command was recognized and applied, false otherwise (caller should refetch).
 *
 * @param {Object} response - parsed rule output (from triggerRule)
 * @param {Object} agParams - the AG-Grid cell params (has .node and .api)
 */
export function applyActionResponse (response, agParams) {
  if (!response || typeof response !== 'object' || !response.command) return false
  const node = agParams?.node
  const api = agParams?.api ?? agParams?.context?.gridApi

  switch (response.command) {
    case 'UPDATE_FIELD_VALUE':
      if (!node || response.field == null) return false
      node.setDataValue(response.field, response.newValue)
      return true

    case 'UPDATE_ROW':
      if (!node || !response.rowData) return false
      Object.entries(response.rowData).forEach(([key, value]) => {
        node.setDataValue(key, value)
      })
      return true

    case 'REMOVE_ROW':
      if (!api || !node) return false
      api.applyTransaction({ remove: [node.data] })
      return true

    default:
      return false
  }
}

/**
 * One-admin-required guard. Vue2 PG&E backend rejects an attempt to remove the
 * last active company admin. We mirror that check on the client to avoid a
 * confusing failure-after-confirm experience.
 *
 * @param {Object} action - the action object the user clicked
 * @param {Object} rowData - the row's data object
 * @param {Object} context - the grid context (has companyId, adminCount)
 */
export function isLastAdminRemoveBlocked (action, rowData, context) {
  const label = String(action?.label ?? '').toLowerCase()
  if (label !== 'remove') return false
  const role = String(rowData?.Type ?? '').toLowerCase()
  if (role !== 'admin') return false
  const adminCount = Number(context?.adminCount ?? 0)
  return adminCount <= 1
}

/**
 * Sanitize HTML for v-html bindings. Strips disallowed tags/attributes using a
 * conservative inline allowlist (b, i, strong, em, a). For richer HTML, view
 * authors can call the shared `$sanitize` global directly.
 *
 * Kept inline to avoid taking a workspace-only dompurify dependency from a
 * customer-extension file.
 */
export function sanitizeHtml (dirty) {
  if (dirty == null) return ''
  const str = String(dirty)
  const ALLOWED = /^(\/?)(b|i|em|strong|a|br|p|span)(\s+href="[^"]*"|\s+target="[^"]*")*\s*$/i
  return str.replace(/<([^<>]+)>/g, (match, inner) => (ALLOWED.test(inner) ? match : ''))
}

/**
 * Cheap "looks like HTML" check used to decide whether to render a confirmation
 * string with v-html or as plain text. Avoids unnecessary sanitization overhead.
 */
export function containsHtml (str) {
  return typeof str === 'string' && /<\/?[a-z][\s\S]*>/i.test(str)
}
